import { ElementType, ReactElement, memo, useEffect } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import DashboardIcon from 'components/Icons/DashboardIcon';
import DashboardIconActive from 'components/Icons/DashboardIconActive';
import BlocksIcon from 'components/Icons/BlocksIcon';
import BlocksIconActive from 'components/Icons/BlocksIconActive';
import DimensionsIcon from 'components/Icons/DimensionsIcon';
import DimensionsIconActive from 'components/Icons/DimensionsIconActive';
import ImportDataIcon from 'components/Icons/ImportDataIcon';
import ImportDataIconActive from 'components/Icons/ImportDataIconActive';
import { FetchFormulaList, getFormulaList } from 'redux/PlannerModeSlice';
import { FetchDimensionList, handleBuilderSelectedDimension } from 'redux/BuilderModeSlice';
import { selectedFileId } from 'redux/DataImportSlice';
import { getDashboard, setSelectedPage } from 'redux/DashboardSliceV2';
import { Flags } from 'models/Global.interface';
import useDashboardData from 'pages/ModelOverviewPage/Dashboard-v2/hooks/useDashboardData';

import { TabType } from '../types';

import NavigationArrowDownIcon from './NavigationArrowDownIcon';

interface NavigationBarProperties {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

interface NavigationIconProperties {
  width?: number;
  height?: number;
}

interface NavigationTab {
  key: TabType;
  label: string;
  icon: (properties: NavigationIconProperties) => ReactElement;
  activeIcon: (properties: NavigationIconProperties) => ReactElement;
}

interface NavigationDropdownSection {
  id: string | number;
  title: string;
  source?: any;
}

const ACTIVE_TAB_COLOR = '#8B5CF6';
const DROPDOWN_ROW_HOVER_STYLE = { bg: '#F5F3FF', color: ACTIVE_TAB_COLOR };

const tabs: NavigationTab[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: DashboardIcon,
    activeIcon: DashboardIconActive,
  },
  {
    key: 'blocks',
    label: 'Blocks',
    icon: BlocksIcon,
    activeIcon: BlocksIconActive,
  },
  {
    key: 'dimensions',
    label: 'Dimensions',
    icon: DimensionsIcon,
    activeIcon: DimensionsIconActive,
  },
  {
    key: 'import',
    label: 'Data',
    icon: ImportDataIcon,
    activeIcon: ImportDataIconActive,
  },
];

const NavigationFlex = Flex as ElementType;
const NavigationBox = Box as ElementType;
const NavigationTooltip = Tooltip as ElementType;

const toDropdownSections = (
  fallbackIdPrefix: string,
  items: any[],
  getTitle: (item: any) => string | undefined,
): NavigationDropdownSection[] => {
  return (items || [])
    .map((item: any, index: number) => ({
      id: item?.id ?? `${fallbackIdPrefix}-${index}`,
      title: getTitle(item) || '',
      source: item,
    }))
    .filter((section) => Boolean(section.title));
};

const sortDashboardPages = (pages: any[] | undefined) =>
  [...(pages || [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

const buildDropdownSections = (
  tabKey: TabType,
  formulaList: any,
  dashboardPages: any[] | undefined,
  uiRevamp: boolean,
): NavigationDropdownSection[] => {
  switch (tabKey) {
    case 'blocks':
      return toDropdownSections('block', formulaList?.blocks, (block) => block?.name);
    case 'dimensions':
      return toDropdownSections(
        'dimension',
        formulaList?.dimensions || formulaList?.dimension,
        (dimension) => dimension?.name,
      );
    case 'import':
      return toDropdownSections(
        'data-source',
        formulaList?.data_source || formulaList?.data_sources,
        (dataSource) => dataSource?.file_name,
      );
    case 'dashboard':
      if (!uiRevamp) {
        return [];
      }
      return toDropdownSections('page', sortDashboardPages(dashboardPages), (page) => page?.name);
    default:
      return [];
  }
};

const NavigationBar = memo(({ activeTab, onTabChange }: NavigationBarProperties): ReactElement => {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const flags = useFlags() as Flags;
  const uiRevamp = Boolean(flags?.uiRevamp);
  const formulaList = useSelector(getFormulaList);
  const dashboard = useSelector(getDashboard);
  useEffect(() => {
    if (modelId) {
      dispatch(FetchFormulaList(Number(modelId)));
    }
  }, [dispatch, modelId]);
  useDashboardData({ enable: Boolean(flags?.uiRevamp) });

  const handleDropdownSectionClick = async (
    tabKey: TabType,
    section: NavigationDropdownSection,
  ) => {
    if (tabKey === 'dashboard' && modelId && uiRevamp) {
      const page = section.source;
      dispatch(setSelectedPage(page));
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data = planFilters ? JSON.parse(planFilters) : {};
        data[Number(modelId)] = {
          ...data[Number(modelId)],
          selectedPageId: page?.id,
        };
        localStorage.setItem('global-modal-filters', JSON.stringify(data));
      } catch {
        // Ignore localStorage errors (private mode, quota, etc.)
      }
      onTabChange('dashboard');
      return;
    }

    if (tabKey === 'blocks' && modelId) {
      navigate(`/model/${modelId}/block/${section.id}/plan`);
      return;
    }

    if (tabKey === 'dimensions' && modelId) {
      const response = await dispatch(FetchDimensionList(modelId));
      const selectedDimension =
        response?.payload?.dimensions?.find(
          (dimension: any) => dimension?.id === Number(section.id),
        ) || section.source;

      await dispatch(handleBuilderSelectedDimension(selectedDimension));
      onTabChange('dimensions');
      return;
    }

    if (tabKey === 'import') {
      dispatch(
        selectedFileId({
          id: section.id,
          name: section.title,
          deatsilsPage: true,
        }),
      );
      onTabChange('import');
    }
  };

  const renderedTabs = tabs.map(
    ({ key, label, icon: IconComponent, activeIcon: ActiveIconComponent }) => {
      const isActive = activeTab === key;
      const IconToRender = isActive ? ActiveIconComponent : IconComponent;
      const dropdownSections = buildDropdownSections(key, formulaList, dashboard?.pages, uiRevamp);

      return (
        <NavigationFlex
          key={key}
          alignItems='center'
          justifyContent='center'
          gap='8px'
          // h='35px'
          px='20px'
          py='8px'
          borderRadius='12px'
          cursor='pointer'
          flexShrink={0}
          bg={isActive ? ACTIVE_TAB_COLOR : 'transparent'}
          color='#121826'
          _hover={{ bg: isActive ? ACTIVE_TAB_COLOR : 'gray.100' }}
          _active={{ bg: isActive ? ACTIVE_TAB_COLOR : 'gray.200' }}
          onClick={() => onTabChange(key)}
        >
          <Box display='flex' alignItems='center' justifyContent='center' w='16px' h='16px'>
            <IconToRender width={16} height={16} />
          </Box>

          <Box
            as='span'
            color={isActive ? 'white' : '#121826'}
            fontSize='13px'
            fontWeight='500'
            lineHeight='1'
            whiteSpace='nowrap'
            textAlign='center'
          >
            {label}
          </Box>
          <NavigationBox
            role='group'
            position='relative'
            display='flex'
            alignItems='center'
            justifyContent='center'
            w='10px'
            h='6px'
          >
            <NavigationArrowDownIcon isActive={isActive} />
            {dropdownSections.length > 0 && (
              <NavigationBox
                display='none'
                _groupHover={{ display: 'block' }}
                position='absolute'
                top='6px'
                left='50%'
                transform='translateX(-50%)'
                zIndex='dropdown'
                pt='12px'
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                <NavigationBox
                  bg='white'
                  border='1px solid #E5E7EB'
                  borderRadius='10px'
                  boxShadow='0px 8px 24px rgba(15, 23, 42, 0.16)'
                  color='#121826'
                  minW='220px'
                  maxW='320px'
                  maxH='320px'
                  overflowY='auto'
                  p='8px'
                >
                  {dropdownSections.map((section) => (
                    <NavigationBox
                      key={section.id}
                      cursor='pointer'
                      py='6px'
                      px='6px'
                      borderRadius='6px'
                      _hover={DROPDOWN_ROW_HOVER_STYLE}
                      onClick={() => handleDropdownSectionClick(key, section)}
                    >
                      <NavigationTooltip label={section.title} hasArrow placement='right'>
                        <NavigationBox
                          fontSize='14px'
                          fontWeight='400'
                          lineHeight='20px'
                          noOfLines={2}
                        >
                          {section.title}
                        </NavigationBox>
                      </NavigationTooltip>
                    </NavigationBox>
                  ))}
                </NavigationBox>
              </NavigationBox>
            )}
          </NavigationBox>
        </NavigationFlex>
      );
    },
  );

  return (
    <NavigationFlex
      alignItems='center'
      width='fit-content'
      bg='white'
      borderRadius='12px'
      flexWrap='nowrap'
      border='1px solid #D9D9D9'
      outline='1px solid #D9D9D9'
      outlineOffset='-1px'
      gap='1px'
      overflow='visible'
      p='0'
    >
      {renderedTabs}
    </NavigationFlex>
  );
});

NavigationBar.displayName = 'NavigationBar';

export default NavigationBar;
